import React, { useEffect, useRef, useState } from "react";
import {
  ArmorLogoIcon,
  SendIcon,
  MessageIcon,
  PackageIcon,
  TargetIcon,
  CheckIcon,
  LinkIcon,
  InboxIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserIcon,
  LockIcon,
  TicketIcon,
  EyeIcon,
  EyeOffIcon,
  AlertIcon,
  ChevronRightIcon,
  Spinner,
  DashboardIcon,
  BellIcon,
  SuccessIcon
} from "./Icons";
import { t, useLang, setLang, LANGUAGES } from "../i18n";
import { S } from "../theme/palette";
import { apiFetchJson } from "../api";
import TurnstileWidget from "./TurnstileWidget";
import { productImage } from "../demo/demoMedia";

const UmbrellaLogoIcon = ({ size = 28 }) => <ArmorLogoIcon size={size} />;

const buildFeatureCards = () => [
  { title: "Sender", desc: t("Массовая рассылка с персонализацией, шаблонами и автосценариями по каждому аккаунту."), icon: SendIcon },
  { title: t("Мультиаккаунт-инбокс"), desc: t("Все диалоги, уведомления и лиды в одном окне с быстрым переключением контекстов."), icon: InboxIcon },
  { title: t("Объявления"), desc: t("Публикация, редактирование и контроль статусов в едином рабочем процессе."), icon: PackageIcon },
  { title: t("Прокси и сессии"), desc: t("Мониторинг прокси, статусов аккаунтов и соединений с автопроверкой пула."), icon: LinkIcon },
  { title: t("Автоответы"), desc: t("Гибкие сценарии ответов, статусы по лидам и передача диалога оператору."), icon: CheckIcon },
  { title: t("Парсер и граббер"), desc: t("Сбор и структурирование данных из карточек объявлений в рабочие списки."), icon: TargetIcon }
];

const buildWorkflowSteps = () => [
  { id: "01", title: t("Регистрация"), desc: t("Создайте доступ и войдите в рабочую панель.") },
  { id: "02", title: t("Подключение"), desc: t("Добавьте аккаунты, прокси и настройте сценарии.") },
  { id: "03", title: t("Запуск задач"), desc: t("Стартуйте публикацию, Sender и автообработку сообщений.") },
  { id: "04", title: t("Контроль"), desc: t("Следите за статусами, лидами и ошибками в реальном времени.") }
];

const buildAuthSidePoints = () => [
  { icon: SendIcon, text: t("Задачи Sender по аккаунтам") },
  { icon: MessageIcon, text: t("Инбокс, лиды и автоответы") },
  { icon: PackageIcon, text: t("Публикация и статусы объявлений") }
];

const TELEGRAM_HANDLE = "umbrellapushbot";

// Логотип Telegram — для кнопки входа через бота.
const TelegramLogo = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="#2AABEE" />
    <path
      d="M5.43 11.72 16.3 7.53c.5-.18.95.12.79.89l-1.85 8.71c-.14.62-.5.77-1.02.48l-2.82-2.08-1.36 1.31c-.15.15-.28.28-.57.28l.2-2.86 5.22-4.71c.23-.2-.05-.31-.35-.11l-6.45 4.06-2.78-.87c-.6-.19-.62-.6.13-.9Z"
      fill="#fff"
    />
  </svg>
);
const TELEGRAM_URL = `https://t.me/${TELEGRAM_HANDLE}`;

const buildFooterLinks = () => [
  { label: t("Возможности"), href: "#auth-features" },
  { label: t("Как работает"), href: "#auth-how" },
  { label: `@${TELEGRAM_HANDLE}`, href: TELEGRAM_URL, external: true },
  { label: t("Условия"), href: "#auth-terms" }
];

// Макет панели на первом экране главной: меню, список диалогов с фото товаров
// и пометками, переписка с подсказкой ответа и две всплывающие карточки. Не
// картинка, а разметка в цветах темы — поэтому акцент меняется вместе с ней.
// Фото товаров — те же нарисованные, что в демо.
const LANDING_MOCK_ROWS = [
  { name: "Tobias K.", text: "Wäre 360 € inkl. Versand ok?", intent: "Versand", image: productImage("smartphone", 250, 0), unread: true, active: true },
  { name: "Sabine", text: "Kann ich es heute Abend abholen?", intent: "Abholung", image: productImage("sofa", 28, 0), unread: true },
  { name: "Murat Yilmaz", text: "Hab dir ein Angebot geschickt 👍", intent: "Angebot", image: productImage("gamepad", 220, 0), unread: false },
  { name: "Anna-Lena", text: "Morgen 18 Uhr passt.", intent: "", image: productImage("bike", 205, 0), unread: false }
];

const LandingMock = ({ compact = false }) => (
  <div className={`landing-mock${compact ? " is-compact" : ""}`} aria-hidden="true">
    <div className="landing-mock-window">
      <div className="landing-mock-bar">
        <span /><span /><span />
        <em>umbrella</em>
      </div>
      <div className="landing-mock-body">
        <div className="landing-mock-rail">
          <i className="landing-mock-logo"><ArmorLogoIcon size={18} /></i>
          <i><DashboardIcon size={14} /></i>
          <i><LinkIcon size={14} /></i>
          <i className="is-active"><MessageIcon size={14} /></i>
          <i><SendIcon size={14} /></i>
          <i><PackageIcon size={14} /></i>
        </div>
        <div className="landing-mock-list">
          <div className="landing-mock-head">
            <b>{t("Диалоги")}</b>
            <span>{t("2 новых")}</span>
          </div>
          {LANDING_MOCK_ROWS.map((row) => (
            <div key={row.name} className={`landing-mock-row${row.active ? " is-active" : ""}`}>
              <img src={row.image} alt="" />
              <span className="landing-mock-row-text">
                <b>{row.name}</b>
                <small>{row.text}</small>
              </span>
              <span className="landing-mock-row-side">
                {row.intent ? <em>{row.intent}</em> : null}
                {row.unread ? <i /> : null}
              </span>
            </div>
          ))}
        </div>
        {!compact && (
          <div className="landing-mock-thread">
            <div className="landing-mock-thread-head">
              <b>Tobias K.</b>
              <small>iPhone 13 · 390 €</small>
            </div>
            <div className="landing-mock-bubble is-in">Hallo, ist das iPhone noch zu haben?</div>
            <div className="landing-mock-bubble is-out">Hallo Tobias, ja, ist noch da 🙂</div>
            <div className="landing-mock-bubble is-in">Wäre 360 € inkl. Versand ok?</div>
            <div className="landing-mock-composer">
              <span>Hallo! Ja, Versand ist möglich…</span>
              <kbd>Tab</kbd>
            </div>
          </div>
        )}
      </div>
    </div>
    <div className="landing-float landing-float-message">
      <span className="landing-float-icon"><BellIcon size={14} /></span>
      <span>
        <b>{t("Новое сообщение")}</b>
        <small>Sabine · Kann ich es heute abholen?</small>
      </span>
    </div>
    <div className="landing-float landing-float-published">
      <span className="landing-float-icon is-success"><SuccessIcon size={14} /></span>
      <span>
        <b>{t("Объявление опубликовано")}</b>
        <small>MacBook Air M1 · 549 €</small>
      </span>
    </div>
  </div>
);

// Поле формы входа: подпись, иконка слева, у пароля — «показать». Вынесено из
// AuthPortal, чтобы при каждой отрисовке формы поле не создавалось заново и не
// теряло фокус посреди ввода.
const AuthField = ({
  label,
  icon: Icon,
  type = "text",
  value,
  onChange,
  onKeyDown,
  placeholder = "",
  autoComplete = "off",
  autoFocus = false,
  revealable = false
}) => {
  const [revealed, setRevealed] = useState(false);
  const inputType = revealable ? (revealed ? "text" : "password") : type;
  return (
    <label className="auth-field">
      <span className="auth-field-label">{label}</span>
      <span className="auth-field-control">
        {Icon ? <Icon size={16} className="auth-field-icon" /> : null}
        <input
          className="auth-field-input"
          type={inputType}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          spellCheck={false}
        />
        {revealable && (
          <button
            type="button"
            className="auth-field-toggle"
            onClick={() => setRevealed((prev) => !prev)}
            aria-label={revealed ? t("Скрыть пароль") : t("Показать пароль")}
            title={revealed ? t("Скрыть пароль") : t("Показать пароль")}
          >
            {revealed ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
          </button>
        )}
      </span>
    </label>
  );
};

const AuthPortal = ({
  isPhoneView = false,
  authError = "",
  onLogin,
  onRegister,
  onTelegramAuthorized,
  appVersion = "vdev",
  // "auth" — сразу форма входа, без главной (мини-апп Telegram, вход по
  // Telegram не удался).
  initialScreen = "landing",
  initialTab = "login",
  // «Открыть демо» — сразу в песочницу, без аккаунта.
  onStartGuestDemo,
  // Панель открыта мини-аппом Telegram: «Открыть демо» и «Войти через
  // Telegram» заводят аккаунт по Telegram и входят в одно нажатие, без формы.
  onTelegramMiniApp
}) => {
  // Подписка на язык: переводы в разметке пересчитываются при переключении,
  // а списки ниже собираются заново на каждую отрисовку — иначе, собранные
  // при загрузке модуля, они оставались бы на первом языке.
  const lang = useLang();
  const featureCards = buildFeatureCards();
  const workflowSteps = buildWorkflowSteps();
  const authSidePoints = buildAuthSidePoints();
  const footerLinks = buildFooterLinks();
  const [screen, setScreen] = useState(initialScreen === "auth" ? "auth" : "landing");
  const [tab, setTab] = useState(initialTab === "register" ? "register" : "login");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");

  // Проверка «не бот» (Cloudflare Turnstile). Включена ли она и с каким
  // ключом — говорит сервер: пока ключей нет, формы работают как раньше.
  const [captcha, setCaptcha] = useState({ enabled: false, siteKey: "" });
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const loadCaptchaConfig = async () => {
    try {
      const data = await apiFetchJson("/api/auth/captcha", { skipAuth: true, timeoutMs: 10000, allowBaseFallback: true });
      const siteKey = String(data?.siteKey || "").trim();
      setCaptcha({ enabled: Boolean(data?.enabled && siteKey), siteKey });
    } catch (_) {
      // Сервер недоступен — вход всё равно не пройдёт; попробуем при отправке.
    }
  };
  useEffect(() => {
    loadCaptchaConfig();
  }, []);
  // Токен одноразовый: после каждой попытки — новый виджет и новая проверка.
  const renewCaptcha = () => {
    setCaptchaToken("");
    setCaptchaResetKey((value) => value + 1);
  };
  const handleCaptchaFailure = (result) => {
    if (!/^CAPTCHA_/.test(String(result?.code || ""))) return;
    // Проверку включили, пока страница была открыта, — подтягиваем ключ.
    if (!captcha.enabled) loadCaptchaConfig();
  };
  const handleCaptchaError = (code) => {
    setFeedback(code === "script"
      ? t("Не удалось загрузить проверку от ботов. Обновите страницу или отключите блокировщик рекламы.")
      : t("Проверка от ботов не загрузилась. Обновите страницу."));
  };
  const captchaMissing = captcha.enabled && !captchaToken;

  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    referralCode: ""
  });

  // Пришёл ли человек с кнопки «Открыть демо»: тогда после регистрации (или
  // входа в аккаунт без подписки) панель сразу открывается в демо, минуя
  // экран «Подписка не активна».
  const [demoIntent, setDemoIntent] = useState(false);

  const openAuth = (targetTab, { demo = false } = {}) => {
    setFeedback("");
    setDemoIntent(demo);
    setTab(targetTab);
    setScreen("auth");
  };

  const runTelegramMiniApp = async ({ openDemo = false } = {}) => {
    if (busy) return;
    setBusy(true);
    setFeedback("");
    const result = await onTelegramMiniApp({ openDemo });
    setBusy(false);
    if (!result?.ok) {
      setFeedback(result?.message || t("Ошибка входа через Telegram"));
      setScreen("auth");
    }
  };

  const openDemo = () => {
    if (onStartGuestDemo) onStartGuestDemo();
    else if (onTelegramMiniApp) runTelegramMiniApp({ openDemo: true });
    else openAuth("register", { demo: true });
  };

  const runLogin = async () => {
    const username = String(loginForm.username || "").trim();
    const password = String(loginForm.password || "");

    if (!username || !password) {
      setFeedback(t("Введите логин и пароль"));
      return;
    }
    if (captchaMissing) {
      setFeedback(t("Подтвердите, что вы не робот."));
      return;
    }

    setBusy(true);
    setFeedback("");
    try {
      const result = await onLogin?.({ username, password, captchaToken, openDemo: demoIntent });
      if (!result?.ok) {
        setFeedback(result?.message || t("Не удалось войти"));
        handleCaptchaFailure(result);
      }
    } catch (error) {
      setFeedback(error?.message || t("Не удалось войти"));
    } finally {
      setBusy(false);
      renewCaptcha();
    }
  };

  const runRegister = async () => {
    const username = String(registerForm.username || "").trim();
    const password = String(registerForm.password || "");
    const confirmPassword = String(registerForm.confirmPassword || "");

    if (!username || !password || !confirmPassword) {
      setFeedback(t("Заполните все обязательные поля"));
      return;
    }
    if (password !== confirmPassword) {
      setFeedback(t("Пароли не совпадают"));
      return;
    }
    if (captchaMissing) {
      setFeedback(t("Подтвердите, что вы не робот."));
      return;
    }
    setBusy(true);
    setFeedback("");
    try {
      const result = await onRegister?.({
        username,
        password,
        referralCode: String(registerForm.referralCode || "").trim(),
        captchaToken,
        openDemo: demoIntent
      });
      if (!result?.ok) {
        setFeedback(result?.message || t("Не удалось зарегистрироваться"));
        handleCaptchaFailure(result);
      }
    } catch (error) {
      setFeedback(error?.message || t("Не удалось зарегистрироваться"));
    } finally {
      setBusy(false);
      renewCaptcha();
    }
  };

  // ── Вход и регистрация через Telegram-бота ──
  // Заявку заводим заранее, пока открыт экран входа: тогда кнопка — обычная
  // ссылка на бота, её не блокирует браузер, а на телефоне она сразу
  // открывает приложение Telegram. Заявка живёт 10 минут — обновляем её
  // каждые 8. После нажатия форма ждёт подтверждения в боте и опрашивает
  // сервер; вернулись во вкладку — опрашиваем сразу.
  const TELEGRAM_REFRESH_MS = 8 * 60 * 1000;
  const [telegramLogin, setTelegramLogin] = useState({ available: null, prepared: null, error: "" });
  const [telegramWait, setTelegramWait] = useState(null);
  const telegramPrepareRef = useRef(null);

  const prepareTelegramLogin = () => {
    if (telegramPrepareRef.current) return telegramPrepareRef.current;
    const task = (async () => {
      try {
        const data = await apiFetchJson("/api/auth/telegram/start", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intent: "login" }),
          skipAuth: true,
          timeoutMs: 15000,
          retry: false,
          allowBaseFallback: true
        });
        if (!data?.url || !data?.token || !data?.pollSecret) throw new Error(t("Не удалось начать вход через Telegram"));
        const prepared = {
          url: String(data.url),
          token: String(data.token),
          pollSecret: String(data.pollSecret),
          botUsername: String(data.botUsername || TELEGRAM_HANDLE),
          preparedAt: Date.now()
        };
        setTelegramLogin({ available: true, prepared, error: "" });
        return { prepared };
      } catch (error) {
        const status = Number(error?.status || 0);
        // 503 — бот не настроен, 404 — сервер ещё без этой функции: кнопку прячем.
        const available = status === 503 || status === 404 ? false : true;
        const message = error?.message || t("Не удалось начать вход через Telegram");
        setTelegramLogin({ available, prepared: null, error: message });
        return { error: message };
      } finally {
        telegramPrepareRef.current = null;
      }
    })();
    telegramPrepareRef.current = task;
    return task;
  };

  useEffect(() => {
    if (screen !== "auth" || telegramWait || telegramLogin.available === false) return undefined;
    const prepared = telegramLogin.prepared;
    if (!prepared) {
      // После отказа (например, слишком много попыток) не долбим сервер —
      // новая попытка будет по нажатию.
      if (!telegramLogin.error) prepareTelegramLogin();
      return undefined;
    }
    const delay = Math.max(5000, prepared.preparedAt + TELEGRAM_REFRESH_MS - Date.now());
    const timer = setTimeout(() => {
      setTelegramLogin((prev) => (prev.prepared === prepared ? { ...prev, prepared: null } : prev));
    }, delay);
    return () => clearTimeout(timer);
  }, [screen, telegramWait, telegramLogin]);

  const startTelegramWait = (prepared) => {
    setFeedback("");
    setTelegramLogin((prev) => ({ ...prev, prepared: null }));
    setTelegramWait({ ...prepared, finishing: false });
  };

  const handleTelegramClick = async (event) => {
    if (busy) {
      event.preventDefault();
      return;
    }
    if (onTelegramMiniApp) {
      event.preventDefault();
      runTelegramMiniApp();
      return;
    }
    const prepared = telegramLogin.prepared;
    if (prepared && Date.now() - prepared.preparedAt < TELEGRAM_REFRESH_MS) {
      // Ссылку открывает сам браузер — переход по href. Форму переключаем на
      // ожидание следующим тактом: снятая со страницы ссылка браузер уже никуда
      // не ведёт, а React убрал бы её раньше, чем браузер по ней перейдёт.
      setTimeout(() => startTelegramWait(prepared), 0);
      return;
    }
    event.preventDefault();
    // Заявки ещё нет: окно открываем сразу, в ответ на нажатие, — после
    // ожидания ответа браузер счёл бы его всплывающим и заблокировал.
    const popup = window.open("", "_blank");
    const result = await prepareTelegramLogin();
    if (!result?.prepared) {
      if (popup) popup.close();
      setFeedback(result?.error || t("Не удалось начать вход через Telegram"));
      return;
    }
    if (popup) {
      popup.opener = null;
      popup.location.href = result.prepared.url;
    }
    startTelegramWait(result.prepared);
  };

  const cancelTelegramWait = () => {
    setTelegramWait(null);
    setFeedback("");
  };

  const telegramWaitToken = telegramWait?.token || "";
  const telegramWaitFinishing = Boolean(telegramWait?.finishing);
  useEffect(() => {
    if (!telegramWait || telegramWait.finishing) return undefined;
    const { token, pollSecret } = telegramWait;
    let stopped = false;
    let inFlight = false;
    const finish = (message) => {
      stopped = true;
      if (message) setFeedback(message);
      setTelegramWait(null);
    };
    const poll = async () => {
      if (stopped || inFlight) return;
      inFlight = true;
      try {
        const data = await apiFetchJson("/api/auth/telegram/poll", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, pollSecret }),
          skipAuth: true,
          timeoutMs: 15000,
          retry: false,
          allowBaseFallback: true
        });
        if (stopped) return;
        const status = String(data?.status || "");
        if (status === "approved") {
          stopped = true;
          setTelegramWait((prev) => (prev && prev.token === token ? { ...prev, finishing: true } : prev));
          const result = await onTelegramAuthorized?.(data, { openDemo: demoIntent });
          if (!result?.ok) {
            setFeedback(result?.message || t("Не удалось войти"));
          }
          setTelegramWait(null);
          return;
        }
        if (status === "rejected") {
          finish(t("Вход отменён в Telegram."));
          return;
        }
        if (status === "expired") {
          finish(t("Время на подтверждение вышло. Нажмите кнопку ещё раз."));
        }
      } catch (error) {
        if (stopped) return;
        if (Number(error?.status || 0) === 403) {
          finish(t("Время на подтверждение вышло. Нажмите кнопку ещё раз."));
        }
        // Сбой сети — просто пробуем на следующем шаге.
      } finally {
        inFlight = false;
      }
    };
    const timer = setInterval(poll, 2000);
    const onVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [telegramWaitToken, telegramWaitFinishing]);

  const normalizedAuthError = String(authError || "").trim();
  const suppressPassiveAuthError = /токен не (задан|указан)|требуется вход в систему/i.test(normalizedAuthError);
  const errorText = String(feedback || (suppressPassiveAuthError ? "" : normalizedAuthError)).trim();

  const submitForm = () => {
    if (tab === "register") runRegister();
    else runLogin();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !busy) submitForm();
  };

  /* ─────────────────────────── LANDING ─────────────────────────── */

  // Язык — одна кнопка с кодом того языка, на который она переключит: при
  // русском на ней EN, при английском — RU. На телефоне она стоит в одной
  // строке с логотипом, а кнопки входа уезжают строкой ниже: вчетвером в 375
  // точек они не помещались и наезжали на название.
  const nextLanguage = LANGUAGES.find((option) => option.value !== lang) || LANGUAGES[0];
  const languageSwitch = (
    <button
      type="button"
      className="landing-lang"
      onClick={() => setLang(nextLanguage.value)}
      title={nextLanguage.label}
      aria-label={nextLanguage.label}
      lang={nextLanguage.value}
    >
      {nextLanguage.value.toUpperCase()}
    </button>
  );

  const heroChecks = [
    t("Все диалоги в одном окне"),
    t("Свой прокси у каждого аккаунта"),
    t("Демо без подписки")
  ];

  const renderLanding = () => (
    <div className={`landing${isPhoneView ? " is-phone" : ""}`}>
      <div className="landing-glow" aria-hidden="true" />

      <header className="landing-nav">
        <div className="landing-nav-row">
          <a className="landing-brand" href="#auth-hero">
            <UmbrellaLogoIcon size={26} />
            <span>umbrella</span>
          </a>
          {!isPhoneView && (
            <nav className="landing-nav-links">
              <a href="#auth-features">{t("Возможности")}</a>
              <a href="#auth-how">{t("Как работает")}</a>
              <a href="#auth-demo">{t("Демо")}</a>
              <a href={TELEGRAM_URL} target="_blank" rel="noreferrer">@{TELEGRAM_HANDLE}</a>
            </nav>
          )}
          {isPhoneView ? languageSwitch : (
            <div className="landing-nav-actions">
              {languageSwitch}
              <button type="button" className="landing-btn is-ghost is-small" onClick={() => openAuth("login")}>
                {t("Вход")}
              </button>
              <button type="button" className="landing-btn is-primary is-small" onClick={() => openAuth("register")}>
                {t("Регистрация")}
              </button>
            </div>
          )}
        </div>
        {/* На телефоне «Вход» и «Регистрация» — только кнопками первого
            экрана: в шапке они их дублировали. */}
      </header>

      {/* Первый экран */}
      <section id="auth-hero" className="landing-hero">
        <div className="landing-hero-copy">
          <span className="landing-badge">
            <ShieldCheckIcon size={14} color="currentColor" />
            {t("Панель мультиаккаунтинга")}
          </span>
          <h1 className="landing-title">
            {t("Все аккаунты.")}
            <br />
            {t("Одна")}{" "}
            <span>{t("панель")}</span>.
          </h1>
          <p className="landing-lead">
            {t("Sender, диалоги, публикация объявлений и прокси — в одном рабочем контуре. Быстрый вход, прозрачные статусы, никакой ручной рутины.")}
          </p>
          <div className="landing-actions">
            <button type="button" className="landing-btn is-primary" onClick={() => openAuth("register")}>
              {t("Создать аккаунт")}
              <ChevronRightIcon size={17} />
            </button>
            {/* Демо — сразу, без регистрации: панель на вымышленных данных
                живёт во вкладке, сервер в нём не спрашивается. */}
            <button type="button" className="landing-btn is-accent" onClick={openDemo} disabled={busy}>
              <SparklesIcon size={16} color="currentColor" />
              {busy ? t("Подождите...") : t("Открыть демо")}
            </button>
            <button type="button" className="landing-btn is-ghost" onClick={() => openAuth("login")}>
              {t("Войти")}
            </button>
          </div>
          <ul className="landing-checks">
            {heroChecks.map((text) => (
              <li key={text}>
                <CheckIcon size={13} />
                {text}
              </li>
            ))}
          </ul>
        </div>
        <LandingMock compact={isPhoneView} />
      </section>

      {/* Возможности */}
      <section id="auth-features" className="landing-section">
        <div className="landing-section-head">
          <span className="landing-kicker">{t("Возможности")}</span>
          <h2>{t("Всё для работы с аккаунтами — в одном месте")}</h2>
          <p>{t("То, что обычно разбросано по вкладкам, таблицам и заметкам, собрано в одну панель.")}</p>
        </div>
        <div className="landing-features">
          {featureCards.map((card, index) => (
            <article key={card.title} className="landing-feature" style={{ animationDelay: `${index * 60}ms` }}>
              <span className="landing-feature-icon">
                <card.icon size={19} color="currentColor" />
              </span>
              <h3>{card.title}</h3>
              <p>{card.desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Как работает */}
      <section id="auth-how" className="landing-section">
        <div className="landing-section-head">
          <span className="landing-kicker">{t("Как это работает")}</span>
          <h2>{t("4 шага")} {t("к автоматизации")}</h2>
          <p>{t("От создания доступа до запущенных задач — обычно меньше десяти минут.")}</p>
        </div>
        <ol className="landing-steps">
          {workflowSteps.map((step) => (
            <li key={step.id} className="landing-step">
              <span className="landing-step-num">{step.id}</span>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Демо */}
      <section id="auth-demo" className="landing-section">
        <div className="landing-cta">
          <div className="landing-cta-copy">
            <span className="landing-kicker">{t("Демо")}</span>
            <h2>{t("Посмотрите панель изнутри")}</h2>
            <p>{t("Демо открывается сразу, без регистрации: все разделы работают на вымышленных данных, ничего не уходит на Kleinanzeigen.")}</p>
          </div>
          <div className="landing-cta-actions">
            <button type="button" className="landing-btn is-primary" onClick={openDemo} disabled={busy}>
              <SparklesIcon size={16} color="currentColor" />
              {busy ? t("Подождите...") : t("Открыть демо")}
            </button>
            <button type="button" className="landing-btn is-ghost" onClick={() => openAuth("register")}>
              {t("Создать аккаунт")}
            </button>
          </div>
        </div>
      </section>

      <footer id="auth-terms" className="landing-footer">
        <div className="landing-footer-row">
          <span className="landing-brand">
            <UmbrellaLogoIcon size={20} />
            <span>umbrella</span>
          </span>
          <nav className="landing-footer-links">
            {footerLinks.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noreferrer" : undefined}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
        <div className="landing-footer-bottom">
          <span>{t("© 2026 umbrella. Все права защищены.")}</span>
          <span>{appVersion}</span>
        </div>
      </footer>
    </div>
  );

  /* ──────────────────────────── AUTH ──────────────────────────── */

  const switchTab = (nextTab) => {
    if (nextTab === tab) return;
    setFeedback("");
    setTab(nextTab);
  };

  const renderFormFields = () => {
    if (tab === "register") {
      return (
        <>
          <AuthField
            label={t("Логин")}
            icon={UserIcon}
            value={registerForm.username}
            onChange={(value) => setRegisterForm((prev) => ({ ...prev, username: value }))}
            onKeyDown={handleKeyDown}
            placeholder="username"
            autoComplete="username"
            autoFocus={!isPhoneView}
          />
          <AuthField
            label={t("Пароль")}
            icon={LockIcon}
            value={registerForm.password}
            onChange={(value) => setRegisterForm((prev) => ({ ...prev, password: value }))}
            onKeyDown={handleKeyDown}
            placeholder={t("минимум 6 символов")}
            autoComplete="new-password"
            revealable
          />
          <AuthField
            label={t("Повторите пароль")}
            icon={LockIcon}
            value={registerForm.confirmPassword}
            onChange={(value) => setRegisterForm((prev) => ({ ...prev, confirmPassword: value }))}
            onKeyDown={handleKeyDown}
            placeholder={t("повторите пароль")}
            autoComplete="new-password"
            revealable
          />
          <AuthField
            label={t("Реферальный код (необязательно)")}
            icon={TicketIcon}
            value={registerForm.referralCode}
            onChange={(value) => setRegisterForm((prev) => ({ ...prev, referralCode: value }))}
            onKeyDown={handleKeyDown}
            placeholder={t("код друга")}
          />
        </>
      );
    }

    return (
      <>
        <AuthField
          label={t("Логин")}
          icon={UserIcon}
          value={loginForm.username}
          onChange={(value) => setLoginForm((prev) => ({ ...prev, username: value }))}
          onKeyDown={handleKeyDown}
          placeholder="username"
          autoComplete="username"
          autoFocus={!isPhoneView}
        />
        <AuthField
          label={t("Пароль")}
          icon={LockIcon}
          value={loginForm.password}
          onChange={(value) => setLoginForm((prev) => ({ ...prev, password: value }))}
          onKeyDown={handleKeyDown}
          placeholder="••••••••"
          autoComplete="current-password"
          revealable
        />
      </>
    );
  };

  const submitLabel = tab === "register"
    ? (demoIntent ? t("Создать аккаунт и открыть демо") : t("Создать аккаунт"))
    : t("Войти в систему");

  const renderAuthForm = () => (
    <div className="auth-form">
      {/* Вход и регистрация — две вкладки одной формы; ползунок переезжает
          под выбранную. */}
      <div className={`auth-tabs${tab === "register" ? " is-register" : ""}`} role="tablist" aria-label={t("Вход или регистрация")}>
        <span className="auth-tabs-thumb" aria-hidden="true" />
        <button
          type="button"
          role="tab"
          aria-selected={tab === "login"}
          className={`auth-tab${tab === "login" ? " is-active" : ""}`}
          onClick={() => switchTab("login")}
        >
          {t("Вход")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "register"}
          className={`auth-tab${tab === "register" ? " is-active" : ""}`}
          onClick={() => switchTab("register")}
        >
          {t("Регистрация")}
        </button>
      </div>

      <div key={tab} className="auth-form-body">
        <h2 className="auth-form-title">
          {tab === "register" ? t("Создание аккаунта") : t("С возвращением")}
        </h2>
        <p className="auth-form-subtitle">
          {tab === "register" ? t("Создайте доступ к панели") : t("Войдите в систему и продолжайте работу")}
        </p>

        {demoIntent && (
          <div className="auth-demo-note">
            <span className="auth-demo-note-icon">
              <SparklesIcon size={15} color="currentColor" />
            </span>
            <span className="auth-demo-note-text">
              <b>{t("Демо-доступ")}</b>
              <span>
                {tab === "register"
                  ? t("Сразу после регистрации откроется демо-панель на вымышленных данных.")
                  : t("Если у аккаунта нет подписки, после входа сразу откроется демо-панель.")}
              </span>
            </span>
          </div>
        )}

        {telegramWait ? renderTelegramWait() : (
        <>
        <div className="auth-form-fields">
          {renderFormFields()}

          {captcha.enabled && (
            <TurnstileWidget
              siteKey={captcha.siteKey}
              action={tab === "register" ? "register" : "login"}
              language={lang === "en" ? "en" : "ru"}
              resetKey={captchaResetKey}
              onToken={(token) => {
                setCaptchaToken(token);
                // Проверку прошли — подсказка «подтвердите, что не робот» больше не нужна.
                if (token) setFeedback((prev) => (prev === t("Подтвердите, что вы не робот.") ? "" : prev));
              }}
              onError={handleCaptchaError}
            />
          )}
        </div>

        {errorText && (
          <div className="auth-error" role="alert">
            <AlertIcon size={15} />
            <span>{errorText}</span>
          </div>
        )}

        <button
          type="button"
          className="auth-submit"
          disabled={busy || captchaMissing}
          onClick={submitForm}
        >
          {busy ? <Spinner size={16} /> : null}
          <span>{busy ? t("Подождите...") : submitLabel}</span>
          {!busy && <ChevronRightIcon size={17} />}
        </button>

        {telegramLogin.available !== false && (
          <>
            <div className="auth-or" aria-hidden="true"><span>{t("или")}</span></div>
            <a
              className={`auth-telegram${busy ? " is-disabled" : ""}`}
              href={telegramLogin.prepared?.url || `https://t.me/${TELEGRAM_HANDLE}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleTelegramClick}
              aria-disabled={busy || undefined}
            >
              <TelegramLogo size={20} />
              <span>{tab === "register" ? t("Зарегистрироваться через Telegram") : t("Войти через Telegram")}</span>
            </a>
          </>
        )}

        <div className="auth-form-switch">
          {tab === "register" ? t("Уже есть доступ? ") : t("Ещё нет доступа? ")}
          <button
            type="button"
            className="auth-text-link"
            onClick={() => switchTab(tab === "register" ? "login" : "register")}
          >
            {tab === "register" ? t("Войти") : t("Создать аккаунт")}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );

  // Ожидание подтверждения в боте — вместо полей формы.
  const renderTelegramWait = () => (
    <div className="auth-telegram-wait">
      <span className="auth-telegram-wait-logo"><TelegramLogo size={44} /></span>
      <div className="auth-telegram-wait-title">{t("Подтвердите вход в Telegram")}</div>
      <p className="auth-telegram-wait-text">
        {t("Откройте бота")} <b>@{telegramWait?.botUsername || TELEGRAM_HANDLE}</b>
        {t(", нажмите «Старт» и подтвердите вход — панель откроется сама.")}
      </p>
      <div className="auth-telegram-wait-status" role="status">
        <Spinner size={15} />
        <span>{telegramWait?.finishing ? t("Входим в панель...") : t("Ждём подтверждения в боте...")}</span>
      </div>
      <a
        className="auth-telegram"
        href={telegramWait?.url || `https://t.me/${TELEGRAM_HANDLE}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <TelegramLogo size={20} />
        <span>{t("Открыть Telegram")}</span>
      </a>
      <div className="auth-form-switch">
        <button type="button" className="auth-text-link" onClick={cancelTelegramWait} disabled={telegramWait?.finishing}>
          {t("Отмена")}
        </button>
      </div>
    </div>
  );

  const renderAuth = () => (
    <div className={`auth-screen${isPhoneView ? " is-phone" : ""}`}>
      <div className="auth-screen-glow" aria-hidden="true" />
      <div className="auth-screen-top">
        <button
          type="button"
          className="kl-plain-button kl-press-back auth-back"
          onClick={() => {
            setFeedback("");
            setDemoIntent(false);
            setScreen("landing");
          }}
        >
          <span className="kl-btn-ico">←</span> {t("Назад")}
        </button>
        {languageSwitch}
      </div>

      <div className="auth-screen-center">
        <div className="auth-card">
          <div className="auth-card-main">
            <div className="auth-card-brand">
              <UmbrellaLogoIcon size={isPhoneView ? 26 : 28} />
              <span>umbrella</span>
            </div>
            {renderAuthForm()}
            <div className="auth-card-version">{appVersion}</div>
          </div>

          {!isPhoneView && (
            <aside className="auth-showcase">
              <span className="auth-showcase-kicker">{t("Внутри панели")}</span>
              <h3 className="auth-showcase-title">{t("Sender, диалоги и объявления в одном окне")}</h3>
              <div className="auth-showcase-points">
                {authSidePoints.map((point) => (
                  <div key={point.text} className="auth-showcase-point">
                    <span className="auth-showcase-point-icon">
                      <point.icon size={16} color="currentColor" />
                    </span>
                    <span>{point.text}</span>
                  </div>
                ))}
              </div>
              {/* Живой кусочек панели: так выглядит новое сообщение от
                  покупателя. Только для вида — нажать нельзя. */}
              <div className="auth-showcase-toast" aria-hidden="true">
                <i>T</i>
                <span>
                  <b>Tobias K.</b>
                  <small>Wäre 360 € inkl. Versand ok?</small>
                </span>
                <em />
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="auth-portal-root"
      style={{
        minHeight: "100dvh",
        width: "100%",
        background: S.bg,
        color: "#e2e4e9",
        fontFamily: "'Manrope', 'Sora', 'Segoe UI', sans-serif",
        position: "relative",
        overflowX: "hidden"
      }}
    >
      <div
        aria-hidden="true"
        style={{
          display: screen === "auth" ? "none" : "block",
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(23,25,33,0.45) 1px, transparent 1px), linear-gradient(90deg, rgba(23,25,33,0.45) 1px, transparent 1px)",
          backgroundSize: isPhoneView ? "42px 42px" : "56px 56px",
          maskImage: "radial-gradient(ellipse at 50% 12%, black 30%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 12%, black 30%, transparent 78%)",
          opacity: 0.5
        }}
      />

      {screen === "landing" ? renderLanding() : renderAuth()}
    </div>
  );
};

export default AuthPortal;
